import { ChangeDetectionStrategy, Component, computed, input, output, signal } from "@angular/core";
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from "@angular/cdk/overlay";
import { NestedTreeControl } from "@angular/cdk/tree";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatNestedTreeNode, MatTree, MatTreeNodeDef, MatTreeNodeOutlet, MatTreeNodePadding, MatTreeNodeToggle } from "@angular/material/tree";
import { IPropertyTreeNode } from "../../shared/utils/property-inspector.util";

/**
 * Trigger + floating tree panel for selecting a (possibly nested) feature property path.
 * Stateless from the caller's perspective — provide `nodes` (see {@link buildPropertyTree}) and
 * `selectedPath`, react to `pathSelected`. Reusable for both rule-condition properties and the
 * text-style "label property" picker.
 */
@Component({
  selector: "lib-property-tree-picker",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTree,
    MatNestedTreeNode,
    MatTreeNodeDef,
    MatTreeNodeOutlet,
    MatTreeNodePadding,
    MatTreeNodeToggle,
  ],
  templateUrl: "./property-tree-picker.component.html",
  styleUrl: "./property-tree-picker.component.scss",
})
export class PropertyTreePickerComponent {
  readonly nodes = input.required<IPropertyTreeNode[]>();

  readonly selectedPath = input<string[] | undefined>(undefined);

  readonly placeholder = input("Select a property");

  readonly pathSelected = output<string[]>();

  readonly open = signal(false);

  readonly overlayPositions: ConnectedPosition[] = [
    { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top" },
    { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom" },
  ];

  readonly treeControl = new NestedTreeControl<IPropertyTreeNode>((node) => node.children ?? []);

  readonly triggerLabel = computed(() => this.selectedPath()?.join(".") || this.placeholder());

  protected toggle(): void {
    this.open.update((v) => !v);
  }

  protected close(): void {
    this.open.set(false);
  }

  protected selectLeaf(node: IPropertyTreeNode): void {
    this.pathSelected.emit(node.path);
    this.close();
  }

  protected isSelected(node: IPropertyTreeNode): boolean {
    const selected = this.selectedPath();
    return !!selected && selected.length === node.path.length && selected.every((segment, i) => segment === node.path[i]);
  }
}
